import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Grid
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useContactUpdate } from '../../../hooks/useContactUpdate';
import AddressAutocompleteField, { getCityCoordinates, majorCities } from '../AddressAutocompleteField';
import { Country } from 'country-state-city';
import { contactDetailStyles, animations } from './styles';

const ContactLocation = ({ contact, onContactUpdate, t = {}, compact = false }) => {
    const { updateContactPartial } = useContactUpdate();
    const [editingLocation, setEditingLocation] = useState(false);
    const [editedLocation, setEditedLocation] = useState({
        countryData: null,
        structuredCity: '',
        coordinates: null
    });

    // Initialize location data when editing starts
    const initializeLocationData = () => {
        let countryData = null;
        
        if (contact.structuredCountry) {
            const allCountries = Country.getAllCountries();
            const foundCountry = allCountries.find(
                c => c.name.toLowerCase() === contact.structuredCountry.toLowerCase()
            );
            
            if (foundCountry) {
                countryData = {
                    name: foundCountry.name,
                    code: foundCountry.isoCode,
                    flag: foundCountry.flag,
                    phoneCode: foundCountry.phonecode
                };
            } else {
                countryData = { name: contact.structuredCountry };
            }
        }
        
        setEditedLocation({
            countryData,
            structuredCity: contact.structuredCity || '',
            coordinates: contact.coordinates || null
        });
        setEditingLocation(true);
    };

    const handleAddressAutocompleteChange = (field, value) => {
        if (field === 'country') {
            setEditedLocation(prev => ({
                ...prev,
                countryData: value,
                // Reset city when country changes
                structuredCity: '',
                coordinates: null
            }));
        } else if (field === 'city') {
            const cityName = typeof value === 'string' ? value : (value ? value.name : '');
            const coordinates = value && value.coordinates ? {
                latitude: value.coordinates[0],
                longitude: value.coordinates[1]
            } : null;
            
            setEditedLocation(prev => ({
                ...prev,
                structuredCity: cityName,
                coordinates: coordinates
            }));
        }
    };

    const handleSaveLocation = async () => {
        try {
            const updateData = {
                structuredCountry: editedLocation.countryData ? editedLocation.countryData.name : '',
                structuredCity: editedLocation.structuredCity,
                coordinates: editedLocation.coordinates
            };
            
            // Save country metadata
            if (editedLocation.countryData) {
                if (editedLocation.countryData.code) {
                    updateData.countryCode = editedLocation.countryData.code;
                }
                if (editedLocation.countryData.flag) {
                    updateData.countryFlag = editedLocation.countryData.flag;
                }
                if (editedLocation.countryData.phoneCode) {
                    updateData.countryPhoneCode = editedLocation.countryData.phoneCode;
                }
            }
            
            const updatedContact = await updateContactPartial(contact._id, updateData, contact);
            
            onContactUpdate(updatedContact);
            setEditingLocation(false);
        } catch (error) {
            console.error('Error updating location:', error);
        }
    };

    const handleCancelLocation = () => {
        setEditingLocation(false);
        setEditedLocation({
            countryData: null,
            structuredCity: '',
            coordinates: null
        });
    };

    if (contact.addresses && !compact) {
        return null; // Don't show location if addresses are available
    }

    if (compact) {
        return (
            <Box sx={{ ...contactDetailStyles.listItem, alignItems: 'flex-start', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                    <LocationOnIcon fontSize="small" sx={{ mt: 0.35, color: 'text.secondary' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" sx={contactDetailStyles.itemTitle}>
                                {t.location || 'Location'}
                            </Typography>
                            {!editingLocation && (
                                <IconButton
                                    size="small"
                                    sx={contactDetailStyles.inlineAction}
                                    onClick={initializeLocationData}
                                    title={contact.structuredCity || contact.structuredCountry ? 'Edit location' : 'Add location'}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>

                        {editingLocation ? (
                            <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12}>
                                        <AddressAutocompleteField
                                            type="country"
                                            value={editedLocation.countryData}
                                            onChange={(value) => handleAddressAutocompleteChange('country', value)}
                                            label={t.country || "Country"}
                                            t={t}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <AddressAutocompleteField
                                            type="city"
                                            value={editedLocation.structuredCity ? {
                                                name: editedLocation.structuredCity,
                                                coordinates: editedLocation.coordinates ?
                                                    [editedLocation.coordinates.latitude, editedLocation.coordinates.longitude] : null
                                            } : null}
                                            onChange={(value) => handleAddressAutocompleteChange('city', value)}
                                            selectedCountry={editedLocation.countryData && editedLocation.countryData.code ?
                                                editedLocation.countryData.code : null}
                                            label={t.city || "City"}
                                            t={t}
                                            size="small"
                                            disabled={!editedLocation.countryData || !editedLocation.countryData.code}
                                            helperText={!editedLocation.countryData || !editedLocation.countryData.code
                                                ? (t.selectCountryFirst || "Select country first")
                                                : (editedLocation.structuredCity && !editedLocation.coordinates
                                                    ? (t.coordinatesNotFound || "Coordinates not found")
                                                    : "")}
                                            error={Boolean(editedLocation.structuredCity && !editedLocation.coordinates)}
                                        />
                                    </Grid>
                                </Grid>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveLocation}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={handleCancelLocation}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={contact.structuredCity || contact.structuredCountry ? contactDetailStyles.itemMeta : contactDetailStyles.emptyText}>
                                {[contact.structuredCity, contact.structuredCountry].filter(Boolean).join(', ') || 'No location specified'}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ ...contactDetailStyles.sectionContainer, ...animations.fadeInUp }}>
            <Typography variant="h6" sx={contactDetailStyles.sectionTitle}>
                <LocationOnIcon fontSize="small" />
                {t.location || 'Location'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" component="div" sx={contactDetailStyles.subsectionLabel}>
                            Details
                        </Typography>
                        {!editingLocation && (contact.structuredCity || contact.structuredCountry) && (
                            <IconButton 
                                size="small" 
                                sx={contactDetailStyles.inlineAction}
                                onClick={initializeLocationData}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    
                    {editingLocation ? (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <AddressAutocompleteField
                                        type="country"
                                        value={editedLocation.countryData}
                                        onChange={(value) => handleAddressAutocompleteChange('country', value)}
                                        label={t.country || "Country"}
                                        t={t}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <AddressAutocompleteField
                                        type="city"
                                        value={editedLocation.structuredCity ? { 
                                            name: editedLocation.structuredCity, 
                                            coordinates: editedLocation.coordinates ? 
                                                [editedLocation.coordinates.latitude, editedLocation.coordinates.longitude] : null 
                                        } : null}
                                        onChange={(value) => handleAddressAutocompleteChange('city', value)}
                                        selectedCountry={editedLocation.countryData && editedLocation.countryData.code ? 
                                            editedLocation.countryData.code : null}
                                        label={t.city || "City"}
                                        t={t}
                                        size="small"
                                        disabled={!editedLocation.countryData || !editedLocation.countryData.code}
                                        helperText={!editedLocation.countryData || !editedLocation.countryData.code 
                                          ? (t.selectCountryFirst || "Select country first") 
                                          : (editedLocation.structuredCity && !editedLocation.coordinates 
                                              ? (t.coordinatesNotFound || "Coordinates not found") 
                                              : "")}
                                        error={Boolean(editedLocation.structuredCity && !editedLocation.coordinates)}
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveLocation}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancelLocation}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" component="div" sx={{ ...contactDetailStyles.bodyText, mt: 0.75 }}>
                            {contact.structuredCity && 
                                <span>
                                    {contact.structuredCity}
                                </span>
                            }
                            {contact.structuredCity && contact.structuredCountry && 
                                <span>, </span>
                            }
                            {contact.structuredCountry && 
                                <span>
                                    {contact.structuredCountry}
                                </span>
                            }
                            {!contact.structuredCity && !contact.structuredCountry && (
                                <Box component="span" sx={contactDetailStyles.emptyText}>
                                    No location specified
                                </Box>
                            )}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ContactLocation; 
